import { z } from "zod";
import { CurrencySchema, DecimalSchema, MarketSymbolSchema } from "./branded";
import {
  AdvisorVerdictSchema,
  ApprovalOnTimeoutSchema,
  ApprovalStatusSchema,
  CashflowDetectedBySchema,
  CashflowKindSchema,
  ChatRoleSchema,
  CredentialAuthTypeSchema,
  CredentialKindSchema,
  DecisionPhaseSchema,
  ExecutionModeSchema,
  MarketCountrySchema,
  NotificationChannelSchema,
  NotificationEventTypeSchema,
  NotificationPrioritySchema,
  NotificationStatusSchema,
  OrderSideSchema,
  OrderStatusSchema,
  OrderTypeSchema,
  RunOutcomeSchema,
  RunRouteSchema,
  RunStateSchema,
  StrategyStatusSchema,
  TimeInForceSchema,
  VerifyStatusSchema,
} from "./enums";
import {
  EpochMsSchema,
  IdSchema,
  JsonObjectSchema,
  JsonValueSchema,
  MoneySchema,
} from "./primitives";

/**
 * Canonical entity DTOs as returned by / accepted by the HTTP API (camelCase
 * projections of the §2 D1 tables). Route modules compose these into request
 * and response schemas.
 */

// ── identity / auth ─────────────────────────────────────────────
export const UserSchema = z.object({
  id: IdSchema,
  email: z.email(),
  displayName: z.string().nullable(),
  claimedAt: EpochMsSchema.nullable(),
  createdAt: EpochMsSchema,
});
export type User = z.infer<typeof UserSchema>;

/** `/api/auth/session` view — instance claim state + current principal. */
export const AuthSessionSchema = z.object({
  claimed: z.boolean(),
  authenticated: z.boolean(),
  user: UserSchema.nullable(),
});
export type AuthSession = z.infer<typeof AuthSessionSchema>;

// ── settings, features, notification policy ─────────────────────
/** Per-event-type channel routing + quiet hours (settings.notif_policy). */
export const NotificationPolicySchema = z.object({
  channels: z.partialRecord(NotificationEventTypeSchema, z.array(NotificationChannelSchema)),
  minPriority: NotificationPrioritySchema.default("normal"),
  quietHours: z
    .object({
      start: z.string(), // "HH:mm"
      end: z.string(),
      tz: z.string(),
    })
    .nullable()
    .default(null),
});
export type NotificationPolicy = z.infer<typeof NotificationPolicySchema>;

/** A KST hour-of-day. Korea has no DST, so a fixed +09:00 makes this unambiguous. */
export const KstHourSchema = z.number().int().min(0).max(23);

/** ISO weekday in KST: 1 = Monday … 7 = Sunday. */
export const KstWeekdaySchema = z.enum(["1", "2", "3", "4", "5", "6", "7"]);

/**
 * When to take an automatic portfolio snapshot.
 *
 * Deliberately a **fixed wall-clock schedule**, not a market-session anchor. The previous
 * design fired on the calendar's regular-market open/close, which meant a calendar outage
 * (or a mis-parsed calendar — see docs/TOSS-SPEC-SYNC.md) silently took the whole snapshot
 * history down: it produced zero automatic snapshots for the entire life of the system, and
 * nothing said so. A wall clock cannot fail that way.
 *
 * Hours are listed **per weekday** rather than as `hours × days`, because the interesting
 * schedules are not rectangular: the US regular session runs 22:30 → 05:00 KST, so Friday's
 * US close lands on *Saturday* morning KST. A Saturday that keeps only its early hours is a
 * natural thing to express here and impossible to express as a product of two lists.
 */
export const SnapshotScheduleSchema = z.object({
  enabled: z.boolean(),
  /**
   * KST hours to snapshot, per weekday. A weekday absent, or mapped to `[]`, takes none.
   *
   * `partialRecord`, not `record`: in zod v4 a `record` over an enum key is EXHAUSTIVE, so
   * `z.record` here would reject every schedule that omits a weekday — including the ones
   * this field's shape exists to express — and the cron's fail-safe would quietly swap them
   * for the default.
   */
  hoursKstByWeekday: z.partialRecord(KstWeekdaySchema, z.array(KstHourSchema).max(24)),
});
export type SnapshotSchedule = z.infer<typeof SnapshotScheduleSchema>;

/**
 * Default: every 6 hours on weekdays, plus Saturday's early hours.
 *
 *   00:00 KST — US regular session in progress (22:30 → 05:00 KST)
 *   06:00 KST — just after the US close        → carries the US closing marks
 *   12:00 KST — KR regular session in progress (09:00 → 15:30 KST)
 *   18:00 KST — just after the KR close        → carries the KR closing marks
 *
 * Saturday keeps 00:00 and 06:00 only: those two are Friday's US session, and dropping them
 * would lose the most valuable snapshot of the week. Sunday has no open market anywhere.
 */
export const DEFAULT_SNAPSHOT_SCHEDULE: SnapshotSchedule = {
  enabled: true,
  hoursKstByWeekday: {
    "1": [0, 6, 12, 18],
    "2": [0, 6, 12, 18],
    "3": [0, 6, 12, 18],
    "4": [0, 6, 12, 18],
    "5": [0, 6, 12, 18],
    "6": [0, 6],
    "7": [],
  },
};

/** Non-secret settings key/value projection (§2 settings table). */
export const SettingsSchema = z.object({
  timezone: z.string(),
  baseCurrency: CurrencySchema,
  tradingArmed: z.boolean(),
  defaultDryRun: z.boolean(),
  notifPolicy: NotificationPolicySchema,
  snapshotSchedule: SnapshotScheduleSchema,
});
export type Settings = z.infer<typeof SettingsSchema>;

/** Derived feature-gate flags for the Settings UI (§8: `{ai,toss,push}`). */
export const FeatureFlagsSchema = z.object({
  ai: z.boolean(),
  toss: z.boolean(),
  push: z.boolean(),
});
export type FeatureFlags = z.infer<typeof FeatureFlagsSchema>;

/** Masked credential projection — secrets never leave the Worker (§10). */
export const CredentialSummarySchema = z.object({
  kind: CredentialKindSchema,
  authType: CredentialAuthTypeSchema,
  maskedTail: z.string().nullable(),
  meta: JsonObjectSchema.nullable(),
  verifyStatus: VerifyStatusSchema.nullable(),
  lastVerifiedAt: EpochMsSchema.nullable(),
  updatedAt: EpochMsSchema,
});
export type CredentialSummary = z.infer<typeof CredentialSummarySchema>;

// ── accounts ────────────────────────────────────────────────────
export const AccountSchema = z.object({
  accountSeq: z.string(),
  number: z.string().nullable(),
  accountType: z.string().nullable(),
  name: z.string().nullable(),
  currency: CurrencySchema.nullable(),
  isPrimary: z.boolean(),
  syncedAt: EpochMsSchema,
});
export type Account = z.infer<typeof AccountSchema>;

// ── portfolio ───────────────────────────────────────────────────
export const HoldingSchema = z.object({
  symbol: MarketSymbolSchema,
  name: z.string(),
  quantity: DecimalSchema,
  lastPrice: DecimalSchema,
  averagePurchasePrice: DecimalSchema,
  marketValue: DecimalSchema,
  profitLoss: DecimalSchema,
  dailyProfitLoss: DecimalSchema,
  cost: DecimalSchema,
  currency: CurrencySchema,
  marketCountry: MarketCountrySchema,
});
export type Holding = z.infer<typeof HoldingSchema>;

export const PortfolioSummarySchema = z.object({
  accountSeq: z.string(),
  baseCurrency: CurrencySchema,
  totalValue: DecimalSchema,
  stocksValue: DecimalSchema,
  cashValue: DecimalSchema,
  fxRate: DecimalSchema.nullable(),
  cumExternal: DecimalSchema,
  cumMarketPnl: DecimalSchema,
  takenAt: EpochMsSchema,
});
export type PortfolioSummary = z.infer<typeof PortfolioSummarySchema>;

export const TimeseriesPointSchema = z.object({
  t: EpochMsSchema,
  value: DecimalSchema,
});
export type TimeseriesPoint = z.infer<typeof TimeseriesPointSchema>;

export const DecompositionPointSchema = z.object({
  t: EpochMsSchema,
  cumExternal: DecimalSchema,
  cumMarketPnl: DecimalSchema,
  twr: z.number(),
});
export type DecompositionPoint = z.infer<typeof DecompositionPointSchema>;

export const AllocationSliceSchema = z.object({
  symbol: MarketSymbolSchema,
  name: z.string(),
  value: DecimalSchema,
  weight: z.number(), // 0..1 fraction of total
});
export type AllocationSlice = z.infer<typeof AllocationSliceSchema>;

export const CashflowEventSchema = z.object({
  id: IdSchema,
  accountSeq: z.string(),
  occurredAt: EpochMsSchema,
  kind: CashflowKindSchema,
  amount: DecimalSchema,
  currency: CurrencySchema,
  baseAmount: DecimalSchema,
  isExternal: z.boolean(),
  detectedBy: CashflowDetectedBySchema,
  note: z.string().nullable(),
  symbol: MarketSymbolSchema.nullable(),
  createdAt: EpochMsSchema,
});
export type CashflowEvent = z.infer<typeof CashflowEventSchema>;

// ── orders & intents ────────────────────────────────────────────
/**
 * A resolved order intent (persisted in `decisions.order_intent` and
 * `approvals.order_intent`). Exactly one of `qty` / `orderAmount` is present.
 */
export const OrderIntentSchema = z.object({
  symbol: MarketSymbolSchema,
  side: OrderSideSchema,
  orderType: OrderTypeSchema,
  timeInForce: TimeInForceSchema.optional(),
  qty: DecimalSchema.optional(),
  orderAmount: DecimalSchema.optional(),
  price: DecimalSchema.optional(),
  currency: CurrencySchema,
  marketCountry: MarketCountrySchema.optional(),
  idemKey: z.string(),
  clientOrderId: z.string().optional(),
  confirmHighValue: z.boolean().default(false),
  /**
   * Rebalance sequencing: this BUY is funded by the same run's SELL legs and
   * must not submit until they settle with fills (auto sell→buy ordering).
   */
  awaitsRunSells: z.boolean().optional(),
});
export type OrderIntent = z.infer<typeof OrderIntentSchema>;

export const OrderSchema = z.object({
  id: IdSchema,
  idemKey: z.string(),
  clientOrderId: z.string(),
  tossOrderId: z.string().nullable(),
  strategyId: IdSchema.nullable(),
  runId: IdSchema.nullable(),
  accountSeq: z.string(),
  symbol: MarketSymbolSchema,
  marketCountry: MarketCountrySchema.nullable(),
  side: OrderSideSchema,
  orderType: OrderTypeSchema,
  timeInForce: TimeInForceSchema.nullable(),
  qty: DecimalSchema.nullable(),
  orderAmount: DecimalSchema.nullable(),
  price: DecimalSchema.nullable(),
  currency: CurrencySchema,
  confirmHighValue: z.boolean(),
  status: OrderStatusSchema,
  filledQty: DecimalSchema,
  avgFillPrice: DecimalSchema.nullable(),
  dryRun: z.boolean(),
  submittedAt: EpochMsSchema.nullable(),
  lastSyncedAt: EpochMsSchema.nullable(),
  closedAt: EpochMsSchema.nullable(),
  createdAt: EpochMsSchema,
  updatedAt: EpochMsSchema,
});
export type Order = z.infer<typeof OrderSchema>;

// ── approvals ───────────────────────────────────────────────────
/**
 * Approval projection. The single-use `token` is intentionally omitted — it is
 * delivered only inside the push payload and must never be read back over the
 * list/detail API.
 */
export const ApprovalSchema = z.object({
  id: IdSchema,
  runId: IdSchema,
  strategyId: IdSchema,
  orderIntent: OrderIntentSchema,
  status: ApprovalStatusSchema,
  expiresAt: EpochMsSchema,
  onTimeout: ApprovalOnTimeoutSchema,
  decidedAt: EpochMsSchema.nullable(),
  decidedVia: z.string().nullable(),
  createdAt: EpochMsSchema,
});
export type Approval = z.infer<typeof ApprovalSchema>;

// ── strategies, runs, decisions ─────────────────────────────────
/**
 * Validated strategy DSL document. Structural validation (indicators, trigger
 * unions, unknown-`kind` rejection) is owned by `@richrich/strategy-core`; the
 * contract layer treats it as an opaque JSON object so `@richrich/contracts`
 * stays a pure Wave-0 package with zero cross-package deps.
 */
export const StrategyDslSchema = JsonObjectSchema;
export type StrategyDsl = z.infer<typeof StrategyDslSchema>;

export const StrategySchema = z.object({
  id: IdSchema,
  slug: z.string(),
  name: z.string(),
  status: StrategyStatusSchema,
  schemaVersion: z.string(),
  dsl: StrategyDslSchema,
  executionMode: ExecutionModeSchema,
  accountSeq: z.string().nullable(),
  budget: MoneySchema.nullable(),
  dryRun: z.boolean(),
  nextRunAt: EpochMsSchema.nullable(),
  createdAt: EpochMsSchema,
  updatedAt: EpochMsSchema,
  archivedAt: EpochMsSchema.nullable(),
});
export type Strategy = z.infer<typeof StrategySchema>;

export const StrategyRunSchema = z.object({
  id: IdSchema,
  strategyId: IdSchema,
  tickId: z.string(),
  state: RunStateSchema,
  scheduledAt: EpochMsSchema,
  startedAt: EpochMsSchema.nullable(),
  finishedAt: EpochMsSchema.nullable(),
  route: RunRouteSchema.nullable(),
  outcome: RunOutcomeSchema.nullable(),
  dryRun: z.boolean(),
  error: z.string().nullable(),
});
export type StrategyRun = z.infer<typeof StrategyRunSchema>;

export const IndicatorReadingSchema = z.object({
  spec: z.string(),
  value: z.number().nullable(),
});
export type IndicatorReading = z.infer<typeof IndicatorReadingSchema>;

export const ConditionTraceNodeSchema = z.object({
  node: z.string(),
  inputs: JsonValueSchema,
  result: z.boolean(),
});
export type ConditionTraceNode = z.infer<typeof ConditionTraceNodeSchema>;

export const RiskCheckSchema = z.object({
  name: z.string(),
  passed: z.boolean(),
  detail: z.string().optional(),
});
export type RiskCheck = z.infer<typeof RiskCheckSchema>;

export const AiVerdictSchema = z.object({
  verdict: AdvisorVerdictSchema,
  sizeMultiplier: z.number().min(0).max(1).optional(),
  reasoning: z.string(),
  model: z.string().optional(),
  latencyMs: z.number().int().nonnegative().optional(),
});
export type AiVerdict = z.infer<typeof AiVerdictSchema>;

/** Append-only audit row explaining a single phase of a run (§2 decisions). */
export const DecisionSchema = z.object({
  id: IdSchema,
  runId: IdSchema,
  strategyId: IdSchema,
  ruleId: z.string().nullable(),
  ts: EpochMsSchema,
  phase: DecisionPhaseSchema,
  indicators: z.array(IndicatorReadingSchema).nullable(),
  conditionTrace: z.array(ConditionTraceNodeSchema).nullable(),
  riskChecks: z.array(RiskCheckSchema).nullable(),
  aiVerdict: AiVerdictSchema.nullable(),
  orderIntent: OrderIntentSchema.nullable(),
  stateFrom: RunStateSchema.nullable(),
  stateTo: RunStateSchema.nullable(),
});
export type Decision = z.infer<typeof DecisionSchema>;

/** Per-strategy performance stats (§2 strategy_stats). */
export const StrategyPnlSchema = z.object({
  strategyId: IdSchema,
  trades: z.number().int().nonnegative(),
  wins: z.number().int().nonnegative(),
  losses: z.number().int().nonnegative(),
  grossProfit: DecimalSchema,
  grossLoss: DecimalSchema,
  winRate: z.number().nullable(),
  payoffRatio: z.number().nullable(),
  realizedPnl: DecimalSchema,
  updatedAt: EpochMsSchema,
});
export type StrategyPnl = z.infer<typeof StrategyPnlSchema>;

/**
 * Portable strategy export envelope — pure logic, provably free of secrets,
 * account bindings, or currency-tied budgets (§10, RESEARCH export rules).
 */
export const StrategyExportSchema = z.object({
  schemaVersion: z.string(),
  slug: z.string(),
  name: z.string(),
  dsl: StrategyDslSchema,
});
export type StrategyExport = z.infer<typeof StrategyExportSchema>;

export const PresetSummarySchema = z.object({
  presetId: z.string(),
  name: z.string(),
  description: z.string(),
  tags: z.array(z.string()).default([]),
  schemaVersion: z.string(),
});
export type PresetSummary = z.infer<typeof PresetSummarySchema>;

// ── chat ────────────────────────────────────────────────────────
export const ChatSessionSchema = z.object({
  id: IdSchema,
  title: z.string().nullable(),
  strategyId: IdSchema.nullable(),
  draftDsl: StrategyDslSchema.nullable(),
  providerModel: z.string().nullable(),
  createdAt: EpochMsSchema,
  updatedAt: EpochMsSchema,
});
export type ChatSession = z.infer<typeof ChatSessionSchema>;

export const ChatMessageSchema = z.object({
  id: IdSchema,
  sessionId: IdSchema,
  role: ChatRoleSchema,
  content: JsonValueSchema, // UIMessage parts JSON
  ts: EpochMsSchema,
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

/**
 * Loose UIMessage shape accepted by the chat stream endpoint. The AI SDK owns
 * the exact part union; the contract requires only `role` + `parts` so the
 * server can pass them to `streamText`.
 */
export const UiMessageSchema = z.object({
  id: z.string().optional(),
  role: ChatRoleSchema,
  parts: JsonValueSchema,
});
export type UiMessage = z.infer<typeof UiMessageSchema>;

// ── notifications & push ────────────────────────────────────────
export const NotificationSchema = z.object({
  id: IdSchema,
  ts: EpochMsSchema,
  eventType: NotificationEventTypeSchema,
  strategyId: IdSchema.nullable(),
  runId: IdSchema.nullable(),
  channel: NotificationChannelSchema,
  title: z.string(),
  body: z.string(),
  data: JsonValueSchema.nullable(),
  dedupeKey: z.string().nullable(),
  priority: NotificationPrioritySchema,
  status: NotificationStatusSchema,
  readAt: EpochMsSchema.nullable(),
  sentAt: EpochMsSchema.nullable(),
});
export type Notification = z.infer<typeof NotificationSchema>;

// ── market data ─────────────────────────────────────────────────
export const PriceSchema = z.object({
  symbol: MarketSymbolSchema,
  lastPrice: DecimalSchema,
  currency: CurrencySchema,
  timestamp: EpochMsSchema.nullable(),
});
export type Price = z.infer<typeof PriceSchema>;

export const CandleSchema = z.object({
  timestamp: EpochMsSchema,
  openPrice: DecimalSchema,
  highPrice: DecimalSchema,
  lowPrice: DecimalSchema,
  closePrice: DecimalSchema,
  volume: DecimalSchema,
  currency: CurrencySchema,
});
export type Candle = z.infer<typeof CandleSchema>;

export const OrderbookEntrySchema = z.object({
  price: DecimalSchema,
  quantity: DecimalSchema,
});
export type OrderbookEntry = z.infer<typeof OrderbookEntrySchema>;

export const OrderbookSchema = z.object({
  symbol: MarketSymbolSchema,
  timestamp: EpochMsSchema.nullable(),
  currency: CurrencySchema,
  asks: z.array(OrderbookEntrySchema), // ascending price
  bids: z.array(OrderbookEntrySchema), // descending price
});
export type Orderbook = z.infer<typeof OrderbookSchema>;

/** A single market-calendar session window (KST). Nullable for US sub-markets. */
export const MarketSessionSchema = z.object({
  open: EpochMsSchema,
  close: EpochMsSchema,
});
export type MarketSession = z.infer<typeof MarketSessionSchema>;

/** One business day from the 3-day calendar window. */
export const MarketDaySchema = z.object({
  date: z.string(), // ISO date (KST)
  isOpen: z.boolean(),
  isHalfDay: z.boolean().default(false),
  regularMarket: MarketSessionSchema.nullable(),
  preMarket: MarketSessionSchema.nullable(),
  afterMarket: MarketSessionSchema.nullable(),
  dayMarket: MarketSessionSchema.nullable(),
});
export type MarketDay = z.infer<typeof MarketDaySchema>;

/** CNN Fear & Greed index (Worker-proxied, §7). */
export const FearGreedSchema = z.object({
  score: z.number(),
  rating: z.string(),
  timestamp: EpochMsSchema,
  previousClose: z.number().nullable(),
  previous1Week: z.number().nullable(),
  previous1Month: z.number().nullable(),
  previous1Year: z.number().nullable(),
});
export type FearGreed = z.infer<typeof FearGreedSchema>;
